import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../lib/supabase';
import { useApi } from '../../lib/use-api';
import { MapView } from '../../components/common/MapView';
import { ArrowLeft, Loader2, Clock, Gauge, Target, Eye, EyeOff, Users } from 'lucide-react';
import { useUnits, kmToDisplay, unitLabel, paceSecPerUnit } from '../../lib/units';
import { formatDuration, formatEventDate } from '../../lib/formatters';

/**
 * RunDetail — full breakdown of a single personal run.
 *
 * Shows:
 *   • Route map (the actual GPS track recorded during the run)
 *   • Big stat row: distance, time, pace (all units-aware)
 *   • Visibility + raw timestamp
 *
 * Reached from Profile → Recent personal runs (each row is a Link to here).
 */
export default function RunDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { units } = useUnits();
  const { data: run, loading } = useApi(api.getRunById, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="run-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!run) {
    return (
      <div className="px-4 pt-4 pb-6 space-y-4" data-testid="run-detail-missing">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="rivl-card p-6 text-center">
          <p className="text-sm text-muted-foreground">This run no longer exists.</p>
        </div>
      </div>
    );
  }

  const distance = kmToDisplay(run.distanceKm, units);
  const paceUnitSec = run.paceSecPerKm ? Math.round(paceSecPerUnit(run.paceSecPerKm, units)) : null;
  const paceLabel = paceUnitSec
    ? `${Math.floor(paceUnitSec / 60)}:${String(paceUnitSec % 60).padStart(2, '0')}`
    : '—';
  const hasTrack = Array.isArray(run.gpsTrack) && run.gpsTrack.length > 1;

  const VisIcon = run.visibility === 'private' ? EyeOff : run.visibility === 'friends' ? Users : Eye;

  return (
    <div className="px-4 pt-4 pb-6 space-y-5" data-testid="run-detail">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="run-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <p className="rivl-label">Personal Run</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{formatEventDate(run.date)}</h1>
      </div>

      {/* Map: real route if recorded, else a friendly empty card */}
      {hasTrack ? (
        <MapView height={280} path={run.gpsTrack} showCurrent currentIndex={run.gpsTrack.length - 1} data-testid="run-detail-map" />
      ) : (
        <div className="rivl-card h-40 flex flex-col items-center justify-center text-center px-6" data-testid="run-detail-no-track">
          <p className="text-sm font-medium">No GPS track for this run</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Older runs may not have a route recorded. New runs save the full GPS path.
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3" data-testid="run-detail-stats">
        <Stat icon={Target} label="Distance" value={`${distance.toFixed(2)}`} unit={unitLabel(units)} />
        <Stat icon={Clock} label="Time" value={formatDuration(run.durationSec)} />
        <Stat icon={Gauge} label="Pace" value={paceLabel} unit={`/${unitLabel(units)}`} />
      </div>

      <div className="rivl-card divide-y divide-border">
        <Row icon={VisIcon} k="Visibility" v={run.visibility} />
        <Row k="Logged" v={new Date(run.date).toLocaleString()} />
        {hasTrack && <Row k="GPS points" v={`${run.gpsTrack.length}`} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, unit }) {
  return (
    <div className="rivl-card p-3.5">
      <div className="flex items-center justify-between">
        <span className="rivl-label text-[9px]">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="font-mono font-bold text-2xl tabular-nums">{value}</span>
        {unit && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{unit}</span>}
      </div>
    </div>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5 gap-3">
      <span className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {k}
      </span>
      <span className="text-sm font-medium font-mono text-right max-w-[60%] truncate">{v}</span>
    </div>
  );
}
